/* eslint-disable no-undef */
import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { t } from 'i18next';

import { CategoryCard } from '../../CompanyEvents/EventLibrary/style';
import { EventPointsWrapper } from '../../MainEvents/styles';
import LazyImage from '../LazyImage/LazyImage';
import { ImageUrl } from '../../../utils/constants';
import {
  Healthicon,
  NutritionIcon,
  BenefitsIcon,
  LearningGrowthIcon,
  DEIIcon,
  StressIcon,
  StressManagementIcon,
  CommunityGivingIcon,
  HeartHealthIcon,
  SelfImprovementIcon,
  EngagementCultureIcon,
  WeightLossIcon,
  PhysicalMentalWellbeingIconNew,
  LearningAndGrowthIconNew,
  DeiIconNew,
  GivingBackIconNew,
  EngagementAndCultureIcon,
  NationalDays,
  BenefitsIconNews,
} from '../../../utils/icons';

/* ── Icon map: key = category_name (trimmed, lowercase for safety) ── */
const categoryIconMap = {
  // Physical / Fitness
  "physical wellness":      PhysicalMentalWellbeingIconNew,
  "fitness":                LearningGrowthIcon,
  "heart health":           HeartHealthIcon,
  "weight loss":            WeightLossIcon,
  // Mental / Stress
  "mental health":          StressManagementIcon,
  "stress management":      StressManagementIcon,
  "stress":                 StressIcon,
  "self-improvement":       SelfImprovementIcon,
  // Nutrition
  "nutrition":              NutritionIcon,
  "healthy habits":         CommunityGivingIcon,
  // Social / Community
  "social wellbeing":       EngagementCultureIcon,
  "community":              GivingBackIconNew,
  "giving back":            GivingBackIconNew,
  "national days":          NationalDays,
  // Workplace / Benefits
  "workplace":              BenefitsIcon,
  "benefits":               BenefitsIconNews,
  "financial wellness":     BenefitsIconNews,
  // Learning / Culture
  "learning & growth":      LearningAndGrowthIconNew,
  "learning and growth":    LearningAndGrowthIconNew,
  "engagement & culture":   EngagementAndCultureIcon,
  "engagement and culture": EngagementAndCultureIcon,
  // DEI
  "dei":                    DeiIconNew,
  "diversity":              DEIIcon,
  // Medical
  "medical":                Healthicon,
  "lifestyle":              DEIIcon,
};

/* ── Background colour map ── */
const categoryColorMap = {
  "physical wellness":      "#FCF2F3",
  "fitness":                "#FCF7F0",
  "heart health":           "#FCF2F3",
  "weight loss":            "#F2F5FB",
  "mental health":          "#FCF7F0",
  "stress management":      "#FCF7F0",
  "stress":                 "#FAF5F8",
  "self-improvement":       "#F5F6E8",
  "nutrition":              "#F1F8F2",
  "healthy habits":         "#F1F8F2",
  "social wellbeing":       "#FBF4F9",
  "community":              "#F1F8F2",
  "giving back":            "#F1F8F2",
  "national days":          "#F1F8F2",
  "workplace":              "#F2F5FB",
  "benefits":               "#FCF7F0",
  "financial wellness":     "#FCF7F0",
  "learning & growth":      "#FCF7F0",
  "learning and growth":    "#FCF7F0",
  "engagement & culture":   "#F1F8F2",
  "engagement and culture": "#F1F8F2",
  "dei":                    "#F5F6E8",
  "diversity":              "#F5F6E8",
  "medical":                "#FCF2F3",
  "lifestyle":              "#F5F6E8",
};

/* ── Border colour map ── */
const categoryBorderColorMap = {
  "physical wellness":      "#E5B9BD",
  "fitness":                "#F1D8B4",
  "heart health":           "#E5B9BD",
  "weight loss":            "#B5C6E3",
  "mental health":          "#F1D8B4",
  "stress management":      "#F1D8B4",
  "stress":                 "#E3B8CE",
  "self-improvement":       "#D6D9A4",
  "nutrition":              "#A4D6A8",
  "healthy habits":         "#A4D6A8",
  "social wellbeing":       "#E3B8CE",
  "community":              "#A4D6A8",
  "giving back":            "#A4D6A8",
  "national days":          "#A4D6A8",
  "workplace":              "#B5C6E3",
  "benefits":               "#F1D8B4",
  "financial wellness":     "#F1D8B4",
  "learning & growth":      "#F1D8B4",
  "learning and growth":    "#F1D8B4",
  "engagement & culture":   "#A4D6A8",
  "engagement and culture": "#A4D6A8",
  "dei":                    "#D6D9A4",
  "diversity":              "#D6D9A4",
  "medical":                "#E5B9BD",
  "lifestyle":              "#D6D9A4",
};

const InitiativeCategoryGrid = ({ categories, onCategoryClick }) => {
  if (!categories?.length) return null;

  return (
    <EventPointsWrapper margin gap={"12px 8px"}>
      {categories.map((category, index) => {
        const key = (category?.category_name || category?.name || "").trim().toLowerCase();

        const IconComponent = categoryIconMap[key];
        const bgColor      = categoryColorMap[key]       || "#F2F5FB";
        const borderColor  = categoryBorderColorMap[key] || "#DCDCDC";

        return (
          <>
            <CategoryCard
              key={index}
              style={{
                border: "0.75px solid #DCDCDC",
                width: "calc((100% - 24px) / 3)",
              }}
              isCardRadius={"12px"}
              isBorderRadius={"4px"}
              cardHeight={"72px"}
              cardPadding={"12px 8px"}
              background={bgColor}
              onClick={() => onCategoryClick && onCategoryClick(category)}
            >
              {IconComponent ? (
                <div className="icon" style={{ border: `1px solid ${borderColor}` }}>
                  {/* <IconComponent /> */}
                  <LazyImage src={ImageUrl + "/" + category.icon}/>
                </div>
              ) : (
                <LazyImage
                  div={"icon"}
                  src={`${ImageUrl}/${category?.icon}`}
                  alt={category?.category_name || category?.name}
                />
              )}
  
              <div className="name" title={t(category?.category_name || category?.name)}>
                {t(category?.category_name || category?.name)}
              </div>
            </CategoryCard>
          </>
        );
      })}
    </EventPointsWrapper>
  );
};

InitiativeCategoryGrid.defaultProps = {
  onCategoryClick: null,
};

InitiativeCategoryGrid.propTypes = {
  categories:       PropTypes.array.isRequired,
  onCategoryClick:  PropTypes.func,
};

export default withTranslation()(InitiativeCategoryGrid);
